/**
 * Privacy ledger: counts every byte the app sends or receives over the network,
 * in the page and in every worker, so "nothing is uploaded" is measured rather
 * than claimed. Each context patches its own network APIs and broadcasts what
 * it saw; the page aggregates.
 */
export type LedgerDirection = 'up' | 'down';

export interface LedgerEntry {
  readonly direction: LedgerDirection;
  readonly host: string;
  /** Bytes, or null when the size could not be determined (still recorded). */
  readonly bytes: number | null;
  readonly name: string;
}

export const LEDGER_CHANNEL = 'shycut-network-ledger';

/**
 * Google Analytics' hosts (src/analytics, only after the visitor agrees).
 * What goes there is a visit count, never a file, so it is listed on its own
 * instead of as an upload.
 */
const ANALYTICS_DOMAINS = ['googletagmanager.com', 'google-analytics.com', 'analytics.google.com'];

export const isAnalyticsHost = (host: string): boolean => ANALYTICS_DOMAINS.some((domain) => host === domain || host.endsWith(`.${domain}`));

/** Size of a request body; null for opaque bodies (streams, form data). */
export function bodySize(body: unknown): number | null {
  if (body === undefined || body === null) return 0;
  if (typeof body === 'string') return new TextEncoder().encode(body).byteLength;
  if (body instanceof Blob) return body.size;
  if (body instanceof ArrayBuffer) return body.byteLength;
  if (ArrayBuffer.isView(body)) return body.byteLength;
  if (body instanceof URLSearchParams) return new TextEncoder().encode(body.toString()).byteLength;
  return null;
}

export interface LedgerSummary {
  /** Bytes uploaded anywhere but analytics. */
  readonly uploadedBytes: number;
  /** Uploads whose size is unknown — any of these is worth showing. */
  readonly unknownUploads: number;
  readonly downloadedBytes: number;
  readonly byHost: ReadonlyArray<{ readonly host: string; readonly downloaded: number; readonly uploaded: number; readonly requests: number }>;
  /** Visit statistics sent to Google Analytics, kept apart from uploads and from {@link byHost}. */
  readonly analytics: { readonly sentBytes: number; readonly requests: number };
}

export function summarize(entries: readonly LedgerEntry[]): LedgerSummary {
  const hosts = new Map<string, { host: string; downloaded: number; uploaded: number; requests: number }>();
  let uploadedBytes = 0;
  let unknownUploads = 0;
  let downloadedBytes = 0;
  const analytics = { sentBytes: 0, requests: 0 };
  for (const entry of entries) {
    if (isAnalyticsHost(entry.host)) {
      analytics.requests++;
      if (entry.direction === 'up') analytics.sentBytes += entry.bytes ?? 0;
      continue;
    }
    const host = hosts.get(entry.host) ?? { host: entry.host, downloaded: 0, uploaded: 0, requests: 0 };
    host.requests++;
    if (entry.direction === 'up') {
      if (entry.bytes === null) unknownUploads++;
      else {
        uploadedBytes += entry.bytes;
        host.uploaded += entry.bytes;
      }
    } else {
      downloadedBytes += entry.bytes ?? 0;
      host.downloaded += entry.bytes ?? 0;
    }
    hosts.set(entry.host, host);
  }
  return { uploadedBytes, unknownUploads, downloadedBytes, byHost: [...hosts.values()].sort((a, b) => b.downloaded - a.downloaded), analytics };
}

const isLocal = (url: URL) => url.protocol === 'blob:' || url.protocol === 'data:';

/**
 * Wraps fetch, XMLHttpRequest and sendBeacon of the given global scope.
 * Idempotent; safe in windows and workers.
 */
export function installNetworkLedger(scope: typeof globalThis = globalThis): void {
  const marker = '__shycutLedger';
  const target = scope as typeof globalThis & { [marker]?: boolean };
  if (target[marker] || typeof BroadcastChannel === 'undefined') return;
  target[marker] = true;
  const channel = new BroadcastChannel(LEDGER_CHANNEL);
  const base = 'location' in scope ? scope.location.href : 'http://localhost/';
  const record = (direction: LedgerDirection, rawUrl: string, bytes: number | null) => {
    let url: URL;
    try {
      url = new URL(rawUrl, base);
    } catch {
      return;
    }
    if (isLocal(url)) return;
    // Analytics carries its data in the address as much as in the body: both count as sent.
    const sent = direction === 'up' && isAnalyticsHost(url.host) ? (bytes ?? 0) + url.search.length : bytes;
    if (direction === 'up' && sent === 0) return;
    const entry: LedgerEntry = { direction, host: url.host, bytes: sent, name: url.pathname.split('/').pop() || url.pathname };
    channel.postMessage(entry);
  };

  const originalFetch = scope.fetch?.bind(scope);
  if (originalFetch) {
    scope.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const url = input instanceof Request ? input.url : String(input);
      const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
      if (method !== 'GET' && method !== 'HEAD') {
        const body = init && 'body' in init ? init.body : input instanceof Request ? input.body : null;
        record('up', url, bodySize(body));
      }
      const response = await originalFetch(input, init);
      const length = Number(response.headers.get('content-length'));
      record('down', response.url || url, Number.isFinite(length) && length > 0 ? length : null);
      return response;
    };
  }

  const xhr = (scope as { XMLHttpRequest?: typeof XMLHttpRequest }).XMLHttpRequest;
  if (xhr) {
    const open = xhr.prototype.open;
    const send = xhr.prototype.send;
    xhr.prototype.open = function (this: XMLHttpRequest & { __ledger?: { method: string; url: string } }, method: string, url: string | URL, ...rest: unknown[]) {
      this.__ledger = { method: method.toUpperCase(), url: String(url) };
      return (open as (...args: unknown[]) => void).call(this, method, url, ...rest);
    } as typeof open;
    xhr.prototype.send = function (this: XMLHttpRequest & { __ledger?: { method: string; url: string } }, body?: Document | XMLHttpRequestBodyInit | null) {
      const info = this.__ledger;
      if (info && info.method !== 'GET' && info.method !== 'HEAD') record('up', info.url, body instanceof Document ? null : bodySize(body));
      if (info) this.addEventListener('loadend', () => record('down', info.url, Number(this.getResponseHeader('content-length')) || null));
      return send.call(this, body as XMLHttpRequestBodyInit | null | undefined);
    };
  }

  const nav = scope.navigator as Navigator | undefined;
  if (nav && typeof nav.sendBeacon === 'function') {
    const beacon = nav.sendBeacon.bind(nav);
    nav.sendBeacon = (url: string | URL, data?: BodyInit | null) => {
      record('up', String(url), bodySize(data));
      return beacon(url, data);
    };
  }
}
